import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import * as transformers from '@attain-apps/transformers'
import { TypeChecker } from '@attain-apps/type-guards'
import gql from 'graphql-tag'

import type * as models from '../../models'
import * as fragments from '../fragments'
import {
  extractFromInfo,
  extractFromInfoAndValidateNotNull,
  passThroughAndValidateNotNull,
} from '../transformers'

const DOC = gql`
  ${fragments.agentForUser.DOC}
  query Me {
    me {
      id
      email
      employeeId
      firstName
      isPlatformAdmin
      isTenant
      lastName
      jobTitle
      phoneNumber
      tags {
        key
        value
      }
      tenants {
        access
        info {
          id
          alias
          code
          name
          color
          createdBy
          monthlyCreditLimit
        }
        agents {
          ...FragmentMeAgentForUser
        }
        zones {
          access
          info {
            id
            alias
            code
            name
            description
            createdBy
          }
          agents {
            ...FragmentMeAgentForUser
          }
        }
      }
    }
  }
`

const TRANSFORMER = graphql.transformers.OUT.select<
  graphql.schema.MeQuery,
  models.User,
  'me'
>(
  'me',
  graphql.transformers.OUT.object({
    cognitoId: () => undefined,
    email: transformers.PASS_THROUGH,
    employeeId: transformers.PASS_THROUGH,
    firstName: transformers.PASS_THROUGH,
    id: transformers.PASS_THROUGH,
    isPlatformAdmin: transformers.PASS_THROUGH,
    isTenant: transformers.PASS_THROUGH,
    jobTitle: transformers.PASS_THROUGH,
    lastName: transformers.PASS_THROUGH,
    name: user => {
      if (!user) {
        // No user => empty name
        return ''
      }
      // Otherwise, we try to create a name by combining first name + last
      // name
      const { firstName, lastName } = user
      return [firstName, lastName]
        .filter(TypeChecker.isNotNullish.raw)
        .join(' ')
    },
    phoneNumber: transformers.PASS_THROUGH,
    tags: graphql.transformers.OUT.always(undefined), // TODO:
    tenants:
      /* -------------------------------------------------------------------------- */
      /*                             tenants transformer                            */
      /* -------------------------------------------------------------------------- */
      graphql.transformers.OUT.default(
        graphql.transformers.OUT.list(
          graphql.transformers.OUT.object<
            NonNullable<graphql.schema.MeQuery['me']['tenants']>[number],
            models.Tenant
          >({
            access: passThroughAndValidateNotNull(),
            agents: fragments.agentForUser.LIST_TRANSFORMER,
            alias: extractFromInfoAndValidateNotNull('alias'),
            code: extractFromInfoAndValidateNotNull('code'),
            color: extractFromInfo('color'),
            createdBy: extractFromInfo('createdBy'),
            id: extractFromInfoAndValidateNotNull('id'),
            monthlyCreditLimit: extractFromInfo('monthlyCreditLimit'),
            name: extractFromInfoAndValidateNotNull('name'),
            /* -------------------------------------------------------------------------- */
            /*                            zones transformer                            */
            /* -------------------------------------------------------------------------- */
            zones: graphql.transformers.OUT.default(
              graphql.transformers.OUT.list(
                graphql.transformers.OUT.object({
                  access: passThroughAndValidateNotNull(),
                  agents: fragments.agentForUser.LIST_TRANSFORMER,
                  alias: extractFromInfoAndValidateNotNull('alias'),
                  code: extractFromInfoAndValidateNotNull('code'),
                  createdBy: extractFromInfo('createdBy'),
                  description: extractFromInfo('description'),
                  id: extractFromInfoAndValidateNotNull('id'),
                  name: extractFromInfoAndValidateNotNull('name'),
                }),
                { onNullish: 'remove' },
              ),
              [],
            ),
          }),
          { onNullish: 'remove' },
        ),
        [],
      ),
  }),
)

export const executor = graphql.createExecutor<
  graphql.schema.MeQuery,
  models.User | undefined
>({
  query: DOC,
  transformer: TRANSFORMER,
})
